import { useEffect, useRef } from 'react';
import axios, { AxiosInstance, AxiosError, InternalAxiosRequestConfig } from 'axios';

const API_BASE_URL = import.meta.env.VITE_API_URL;

// Create axios instance
const apiClient: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor - Add auth token
apiClient.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const token = localStorage.getItem('token');
    if (token && config.headers) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor - Handle token refresh
apiClient.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean };

    // Handle 401 errors
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      try {
        const refreshToken = localStorage.getItem('refreshToken');
        if (!refreshToken) {
          throw new Error('No refresh token available');
        }

        // Refresh token request (normalize to /api/auth/token/refresh/)
        const root = (API_BASE_URL || '').replace(/\/$/, '');
        const apiRoot = root.endsWith('/api') ? root : `${root}/api`;
        const refreshUrl = `${apiRoot}/auth/token/refresh/`;
        const refreshResponse = await axios.post(refreshUrl, {
          refresh: refreshToken,
        });

        const { access, refresh } = refreshResponse.data;

        // Store new tokens
        localStorage.setItem('token', access);
        if (refresh) {
          localStorage.setItem('refreshToken', refresh);
        }

        // Retry original request with new token
        if (originalRequest.headers) {
          originalRequest.headers.Authorization = `Bearer ${access}`;
        }
        return apiClient(originalRequest);
      } catch (refreshError) {
        // Refresh failed, logout user
        localStorage.removeItem('token');
        localStorage.removeItem('refreshToken');
        localStorage.removeItem('user');
        window.location.href = '/';
        return Promise.reject(refreshError);
      }
    }

    return Promise.reject(error);
  }
);

// API methods
export const api = {
  get: <T>(url: string, params?: Record<string, unknown>) =>
    apiClient.get<T>(url, { params }).then(res => res.data),

  post: <T>(url: string, data?: unknown) =>
    apiClient.post<T>(url, data).then(res => res.data),

  /**
   * POST a multipart/form-data body. Lets the browser set the boundary by
   * forcing the JSON Content-Type that the axios instance sets by default
   * to be dropped for this request.
   */
  postForm: <T>(url: string, data: FormData) =>
    apiClient
      .post<T>(url, data, {
        headers: { 'Content-Type': 'multipart/form-data' },
        transformRequest: [(body) => body], // prevent axios from JSON-stringifying FormData
      })
      .then(res => res.data),

  /**
   * PATCH a multipart/form-data body.
   */
  patchForm: <T>(url: string, data: FormData) =>
    apiClient
      .patch<T>(url, data, {
        headers: { 'Content-Type': 'multipart/form-data' },
        transformRequest: [(body) => body],
      })
      .then(res => res.data),

  put: <T>(url: string, data?: unknown) =>
    apiClient.put<T>(url, data).then(res => res.data),

  patch: <T>(url: string, data?: unknown) =>
    apiClient.patch<T>(url, data).then(res => res.data),

  delete: <T>(url: string) =>
    apiClient.delete<T>(url).then(res => res.data),
};

// Hook for using API with cleanup
export function useApi() {
  const abortControllerRef = useRef<AbortController | null>(null);

  useEffect(() => {
    return () => {
      // Cleanup pending requests on unmount
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  const cancelPendingRequests = () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = new AbortController();
    }
  };

  return {
    api,
    apiClient,
    cancelPendingRequests,
  };
}

export default apiClient;
